import React, { forwardRef, memo, useCallback, useMemo } from 'react'

import { twMerge } from 'tailwind-merge'

import { FileDetails } from '@/components/knowledge-base/FileDetails'

import { useMentionList } from '@/extensions/Mention/hooks'
import { getItemsWithQuery } from '@/extensions/Mention/lib'
import {
  type MentionItem,
  type MentionListProps,
  type MentionListRef,
} from '@/extensions/Mention/types'

import { useKnowledgeBaseFiles } from '@/hooks/knowledge-base/useKnowledgeBaseFiles'

import type { PotentiallyUnhydratedFile } from '@/types/File'

export type FileWithType = PotentiallyUnhydratedFile &
  MentionItem & {
    mentionType: 'file'
  }

const selectKnowledgeBaseContent = (
  data: PotentiallyUnhydratedFile[],
): FileWithType[] => {
  return data
    .map((file) => ({
      ...file,
      mentionType: 'file' as const,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

export const MentionList = forwardRef<
  MentionListRef,
  MentionListProps<FileWithType>
>((props, ref) => {
  // we are fetching the items from within the component, rather than fetching them
  // in the parent hooks that populate the items because the tiptap editor caches the
  // items at initial render, which doesn't give us enough time to fetch the items from
  // the server

  if (!props.suggestion || props.suggestion.type !== 'knowledge-base') {
    throw new Error('Knowledge base suggestion args are required')
  }

  const { data: items } = useKnowledgeBaseFiles<FileWithType[]>({
    pursuitId: props.suggestion.pursuitId,
    orgId: props.suggestion.orgId ?? '',
    select: selectKnowledgeBaseContent,
  })

  const filteredItems = useMemo(() => {
    return getItemsWithQuery(items, props.query, props.getLabel)
  }, [items, props.query, props.getLabel])

  const { selectedIndex, selectItem } = useMentionList(
    {
      ...props,
      items: filteredItems,
    },
    ref,
  )

  const handleSelectItem = useCallback(
    (index: number) => () => {
      selectItem(index)
      props.onSelect?.(filteredItems[index])
    },
    [filteredItems, props.onSelect, selectItem],
  )

  return (
    <div
      className={twMerge(
        'flex flex-col gap-1 p-1 w-[424px] max-h-60 rounded-md border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900 overflow-y-auto',
      )}
    >
      {filteredItems.length ? (
        filteredItems.map((item, index) => (
          <button
            type="button"
            className={twMerge(
              'relative flex items-center gap-x-2 w-full rounded-[4px] cursor-pointer select-none py-1 px-2 outline-none text-[13px] text-primary-700 dark:text-primary-300',
              index === selectedIndex
                ? 'bg-primary-200 dark:bg-primary-800'
                : 'hover:bg-primary-100 dark:hover:bg-primary-800',
            )}
            key={index}
            onClick={handleSelectItem(index)}
          >
            <FileDetails file={item} />
          </button>
        ))
      ) : (
        <div
          className={twMerge(
            'relative flex items-center justify-between gap-x-2 w-full text-primary-900 dark:text-primary-100 rounded-[4px] cursor-default select-none px-2 py-1 text-sm outline-none',
          )}
        >
          <div className="font-medium text-primary-500 dark:text-primary-400">
            No files found.
          </div>
        </div>
      )}
    </div>
  )
})

MentionList.displayName = 'Mention.KnowledgeBase.MentionList'
